/**
 * TTL Manager - Async background processor for cache TTL operations
 * 
 * This service batches and processes TTL updates asynchronously to avoid
 * blocking the main thread during Apollo cache merge operations.
 */

import { PerformanceMonitor } from './cacheCleanupService';

interface TTLUpdate {
  cacheId: string;
  ttl: number;
  timestamp: number;
}

export class TTLManager {
  private static instance: TTLManager | null = null;
  private ttlStore: Map<string, number>;
  private pendingUpdates: TTLUpdate[] = [];
  private processingTimer: number | null = null;
  private isProcessing = false;
  
  // Configuration
  private readonly BATCH_SIZE = 100;
  private readonly PROCESS_INTERVAL = 50; // ms
  private readonly MAX_BATCH_TIME = 10; // ms
  
  private constructor(ttlStore: Map<string, number>) {
    this.ttlStore = ttlStore;
  }
  
  static getInstance(ttlStore: Map<string, number>): TTLManager {
    if (!TTLManager.instance) {
      TTLManager.instance = new TTLManager(ttlStore);
    }
    return TTLManager.instance;
  }
  
  /**
   * Queue a TTL update to be processed asynchronously
   */
  queueTTLUpdate(cacheId: string, ttl: number): void {
    this.pendingUpdates.push({
      cacheId,
      ttl,
      timestamp: Date.now(),
    });
    
    // Schedule processing if not already scheduled
    if (!this.processingTimer && !this.isProcessing) {
      this.scheduleProcessing();
    }
  }
  
  /**
   * Schedule batch processing of pending updates
   */
  private scheduleProcessing(): void {
    this.processingTimer = window.setTimeout(() => {
      this.processingTimer = null;
      this.processPendingUpdates();
    }, this.PROCESS_INTERVAL);
  }
  
  /**
   * Process pending TTL updates in batches
   */
  private async processPendingUpdates(): Promise<void> {
    if (this.isProcessing || this.pendingUpdates.length === 0) {
      return;
    }
    
    this.isProcessing = true;
    
    try {
      await PerformanceMonitor.measureAsync('ttl-batch-processing', async () => {
        const startTime = performance.now();
        let processed = 0;
        
        // Process updates in batches
        while (this.pendingUpdates.length > 0 && processed < this.BATCH_SIZE) {
          // Check if we've exceeded max batch time
          if (performance.now() - startTime > this.MAX_BATCH_TIME) {
            break;
          }
          
          const update = this.pendingUpdates.shift();
          if (update) {
            const expiryTime = update.timestamp + update.ttl;
            this.ttlStore.set(update.cacheId, expiryTime);
            processed++;
          }
        }
        
        if (process.env.NODE_ENV === 'development' && processed > 0) {
          console.log(`⚡ TTL Manager: Processed ${processed} updates, ${this.pendingUpdates.length} remaining`);
        }
      });
    } catch (error) {
      console.error('❌ TTL Manager: Error processing updates:', error);
    } finally {
      this.isProcessing = false;
      
      // Schedule next batch if there are more updates
      if (this.pendingUpdates.length > 0) {
        this.scheduleProcessing();
      }
    }
  }
  
  /**
   * Get current stats for monitoring
   */
  getStats(): {
    pendingUpdates: number;
    isProcessing: boolean;
    ttlStoreSize: number;
  } {
    return {
      pendingUpdates: this.pendingUpdates.length,
      isProcessing: this.isProcessing,
      ttlStoreSize: this.ttlStore.size,
    };
  }
  
  /**
   * Force process all pending updates (useful for cleanup)
   */
  async flush(): Promise<void> {
    while (this.pendingUpdates.length > 0) {
      await this.processPendingUpdates();
    }
  }
  
  /**
   * Clear all pending updates
   */
  clear(): void {
    this.pendingUpdates = [];
    if (this.processingTimer) {
      clearTimeout(this.processingTimer);
      this.processingTimer = null;
    }
  }
}